// Edit planner for tablespace + filegroup ops (v2 Slice E2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice E2.
//
// All seven ops append a fresh statement after the source statement (DSE2-7:
// no coalescing by default). Per-engine support / rejection lives in the
// dialect synthesizer.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAddDatabaseFile,
  synthesizeAlterTablespace,
  synthesizeCreateFilegroup,
  synthesizeCreateTablespace,
  synthesizeDropFilegroup,
  synthesizeDropTablespace,
  synthesizeModifyDatabaseFile,
} from './dialect.tablespace';

type TablespaceEdit = Extract<
  DdlEdit,
  {
    op:
      | 'create-tablespace'
      | 'alter-tablespace'
      | 'drop-tablespace'
      | 'create-filegroup'
      | 'drop-filegroup'
      | 'add-database-file'
      | 'modify-database-file';
  }
>;

export function planTablespaceEdit(
  ctx: DdlEditCtx,
  edit: TablespaceEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForTablespace(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForTablespace(ctx: DdlEditCtx, edit: TablespaceEdit): string {
  switch (edit.op) {
    case 'create-tablespace':
      return synthesizeCreateTablespace(ctx.engine, edit.tablespace);
    case 'alter-tablespace':
      return synthesizeAlterTablespace(ctx.engine, edit.tablespaceName, edit.change);
    case 'drop-tablespace':
      return synthesizeDropTablespace(
        ctx.engine,
        edit.tablespaceName,
        edit.ifExists ?? false,
      );
    case 'create-filegroup':
      return synthesizeCreateFilegroup(ctx.engine, edit.filegroup);
    case 'drop-filegroup':
      return synthesizeDropFilegroup(ctx.engine, edit.filegroup);
    case 'add-database-file':
      return synthesizeAddDatabaseFile(ctx.engine, edit.file);
    case 'modify-database-file':
      return synthesizeModifyDatabaseFile(
        ctx.engine,
        edit.database,
        edit.fileName,
        edit.change,
      );
  }
}
